import { useRouterState } from '@tanstack/react-router';
import { useMemo, type ReactNode } from 'react';

import { hasPermission, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { TourProvider, WelcomePrompt, type TourAccess } from '@nucleus-commerce/admin-kit/tour';

import { session } from '@/shared/session/session';

import { adminTourProgress } from '../api/tours-api';
import { adminTourRegistry } from '../definitions';

export interface AdminTourProviderProps {
  /** Runtime config `tours.enabled`; the kill switch during rollout. */
  enabled: boolean;
  /** Runtime config plus mock-mode opt-in (`welcomePromptAllowed`). */
  welcomeAllowed: boolean;
  /** The shell's current store; store tours run there. */
  storeId: string | undefined;
  children: ReactNode;
}

/**
 * Binds the shared tour engine to the admin: the session's permissions, the current store, the
 * `/admin/v1/me/tours` progress routes. Mounted by the shell inside its PermissionsProvider.
 */
export function AdminTourProvider({
  enabled,
  welcomeAllowed,
  storeId,
  children,
}: AdminTourProviderProps) {
  const { me } = session.useSession();
  const { granted } = usePermissions();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const access = useMemo<TourAccess>(
    () => ({ can: (permission, mode) => hasPermission(granted, permission, mode) }),
    [granted],
  );
  const firstName = me?.user.name.split(' ')[0] ?? '';
  const values = useMemo(() => ({ firstName }), [firstName]);
  const supportSession = me?.supportSession != null;
  const active = enabled && me != null && !me.twoFactorRequired;

  return (
    <TourProvider
      registry={adminTourRegistry}
      access={access}
      progressAdapter={adminTourProgress}
      app="admin"
      enabled={active}
      readOnly={supportSession}
      storeId={storeId}
      values={values}
    >
      {children}
      <WelcomePrompt
        allowed={active && welcomeAllowed && !supportSession && pathname === '/org'}
        firstName={firstName}
      />
    </TourProvider>
  );
}
